function isSocketActive(socket) {
  if (!socket) return false;
  return socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING;
}

function safeCall(fn, ...args) {
  if (typeof fn !== "function") return;
  try {
    fn(...args);
  } catch {
  }
}

export function createVoicebotWsService({
  wsUrl,
  onJsonEvent,
  onBinaryAudio,
  onClose,
  onError,
  onOpen,
}) {
  let socket = null;
  let closeInitiator = "server";

  function clearSocketRefs() {
    if (!socket) return;
    socket.onopen = null;
    socket.onmessage = null;
    socket.onerror = null;
    socket.onclose = null;
    socket = null;
  }

  function parseAndHandleJson(raw) {
    try {
      const obj = JSON.parse(raw);
      safeCall(onJsonEvent, obj);
    } catch {
      safeCall(onError, { code: "invalid_json", message: "Invalid JSON message from WS." });
    }
  }

  async function handleMessage(event) {
    if (typeof event.data === "string") {
      parseAndHandleJson(event.data);
      return;
    }

    if (event.data instanceof ArrayBuffer) {
      safeCall(onBinaryAudio, event.data);
      return;
    }

    if (event.data instanceof Blob) {
      try {
        const buf = await event.data.arrayBuffer();
        safeCall(onBinaryAudio, buf);
      } catch {
        safeCall(onError, { code: "blob_decode_failed", message: "Unable to decode binary audio frame." });
      }
    }
  }

  function closeSocket(code = 1000, reason = "client_close") {
    if (!socket) return;
    closeInitiator = "client";
    if (isSocketActive(socket)) {
      try {
        socket.close(code, reason);
      } catch {
      }
    }
  }

  async function connect(token, systemPrompt = "", sessionVars = {}) {
    const bearer = String(token || "").trim();
    const url = String(wsUrl || "").trim();

    if (!bearer) throw new Error("Missing access token for WebSocket start.");
    if (!url) throw new Error("Missing WebSocket URL.");
    if (isSocketActive(socket)) throw new Error("WebSocket is already connected.");

    let ws;
    try {
      ws = new WebSocket(url);
    } catch {
      throw new Error("Invalid WebSocket URL.");
    }

    ws.binaryType = "arraybuffer";
    socket = ws;
    closeInitiator = "server";

    await new Promise((resolve, reject) => {
      let settled = false;
      const finishResolve = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      const finishReject = (err) => {
        if (settled) return;
        settled = true;
        reject(err);
      };

      ws.onopen = () => {
        const payload = {
          type: "start",
          bearer,
        };
        const prompt = String(systemPrompt || "").trim();
        if (prompt) payload.system_prompt = prompt;
        const firstMessage = String(sessionVars?.first_message ?? sessionVars?.firstMessage ?? "").trim();
        if (firstMessage) payload.first_message = firstMessage;
        const clientId = String(sessionVars?.client_id ?? sessionVars?.clientId ?? "").trim();
        const clientName = String(sessionVars?.client_name || "").trim();
        const contactName = String(sessionVars?.contact_name || "").trim();
        if (clientId) payload.client_id = clientId;
        if (clientName) payload.client_name = clientName;
        if (contactName) payload.contact_name = contactName;
        if (typeof sessionVars?.is_third_person === "boolean") {
          payload.is_third_person = sessionVars.is_third_person;
        }

        try {
          ws.send(JSON.stringify(payload));
          safeCall(onOpen, { url });
          finishResolve();
        } catch (err) {
          finishReject(err instanceof Error ? err : new Error("Failed to send WS start payload."));
          closeSocket(1011, "start_send_failed");
        }
      };

      ws.onerror = () => {
        safeCall(onError, { code: "socket_error", message: "WebSocket transport error." });
        if (!settled) {
          finishReject(new Error("WebSocket transport error."));
        }
      };

      ws.onmessage = (event) => {
        void handleMessage(event);
      };

      ws.onclose = (event) => {
        const meta = {
          clientInitiated: closeInitiator === "client",
          code: event.code,
          reason: event.reason || "",
          wasClean: event.wasClean,
        };
        clearSocketRefs();
        safeCall(onClose, event, meta);
      };
    });
  }

  function sendAudio(arrayBuffer) {
    if (!(arrayBuffer instanceof ArrayBuffer)) {
      throw new Error("sendAudio expects ArrayBuffer.");
    }
    if (!socket || socket.readyState !== WebSocket.OPEN) return false;

    socket.send(arrayBuffer);
    return true;
  }

  function stop() {
    if (!socket) return;
    closeInitiator = "client";
    if (socket.readyState === WebSocket.OPEN) {
      try {
        socket.send(JSON.stringify({ type: "stop" }));
      } catch {
      }
    }
    closeSocket(1000, "client_stop");
  }

  function close(code = 1000, reason = "client_close") {
    closeSocket(code, reason);
  }

  return {
    connect,
    sendAudio,
    stop,
    close,
  };
}
